import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Settings, Lock, Users, Shield, Trash2, Power, PowerOff,
  Eye, EyeOff, Plus, Activity, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [showNewAdminForm, setShowNewAdminForm] = useState(false);
  
  // Password change form
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  
  // New admin form
  const [newAdminForm, setNewAdminForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin"
  });

  useEffect(() => {
    loadData();
  }, []);

  const getToken = () => localStorage.getItem("admin_token");
  
  const getAuthHeaders = () => ({
    headers: {
      "Authorization": `Bearer ${getToken()}`
    }
  });

  const loadData = async () => {
    try {
      const token = getToken();
      if (!token) {
        // Try to login with default credentials to get a token
        await attemptAutoLogin();
        return;
      }

      // Load current admin info
      const meResponse = await axios.get(`${API}/admin/me`, getAuthHeaders());
      setCurrentAdmin(meResponse.data);

      // Load admin users if super admin
      if (meResponse.data.role === "super_admin") {
        const usersResponse = await axios.get(`${API}/admin/users`, getAuthHeaders());
        setAdminUsers(usersResponse.data.users);

        // Load admin logs
        const logsResponse = await axios.get(`${API}/admin/logs?limit=20`, getAuthHeaders());
        setAdminLogs(logsResponse.data.logs);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 401) {
        // Token expired or invalid, try auto-login
        await attemptAutoLogin();
      } else {
        toast.error("Erreur lors du chargement des données");
        setLoading(false);
      }
    }
  };

  const attemptAutoLogin = async () => {
    // Auto-login can't work with 2FA — just show expired message
    console.warn("Session expired, 2FA required for re-login");
    toast.error("Session expirée, veuillez vous reconnecter");
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/change-password`,
        passwordForm,
        getAuthHeaders()
      );
      
      toast.success("Mot de passe modifié avec succès");
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: ""
      });
      setShowPasswordFields(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors du changement de mot de passe"));
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(
        `${API}/admin/users`,
        newAdminForm,
        getAuthHeaders()
      );
      
      toast.success("Administrateur créé avec succès");
      setNewAdminForm({
        name: "",
        email: "",
        password: "",
        role: "admin"
      });
      setShowNewAdminForm(false);
      loadData(); // Reload data
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors de la création de l'administrateur"));
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userId}/toggle`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`Compte ${currentStatus ? 'désactivé' : 'activé'} avec succès`);
      loadData(); // Reload data
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors de la modification du statut"));
    }
  };

  const handleDeleteAdmin = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet administrateur ?")) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/users/${userId}`,
        getAuthHeaders()
      );
      
      toast.success("Administrateur supprimé avec succès");
      loadData(); // Reload data
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur lors de la suppression"));
    }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir vous déconnecter de tous les appareils ?")) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/logout-all`,
        {},
        getAuthHeaders()
      );
      
      toast.success("Déconnecté de tous les appareils");
      // Clear local token
      localStorage.removeItem("admin_token");
      // Redirect to login
      window.location.href = "/admin";
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-rose-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configuration du Compte</h1>
          <p className="text-gray-600">Gérez votre compte et la sécurité</p>
        </div>
      </div>

      {/* Current Admin Info */}
      <Card className="border-rose-200">
        <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-rose-600" />
            Informations du Compte
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-600">Nom</Label>
              <p className="font-semibold">{currentAdmin?.name}</p>
            </div>
            <div>
              <Label className="text-gray-600">Email</Label>
              <p className="font-semibold">{currentAdmin?.email}</p>
            </div>
            <div>
              <Label className="text-gray-600">Rôle</Label>
              <p className="font-semibold">
                {currentAdmin?.role === "super_admin" ? "Super Administrateur" : "Administrateur"}
              </p>
            </div>
            <div>
              <Label className="text-gray-600">Dernière connexion</Label>
              <p className="font-semibold">
                {currentAdmin?.last_login 
                  ? format(new Date(currentAdmin.last_login), "dd MMM yyyy HH:mm", { locale: fr })
                  : "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Section */}
      <Card className="border-rose-200">
        <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50">
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-rose-600" />
            Sécurité du Mot de Passe
          </CardTitle>
          <CardDescription>
            Modifiez votre mot de passe pour sécuriser votre compte
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {!showPasswordFields ? (
            <Button
              onClick={() => setShowPasswordFields(true)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              <Lock className="h-4 w-4 mr-2" />
              Modifier le mot de passe
            </Button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="current_password">Mot de passe actuel *</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="new_password">Nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Min. 8 caractères, 1 majuscule, 1 chiffre, 1 symbole
                </p>
              </div>
              
              <div>
                <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe *</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" className="bg-rose-600 hover:bg-rose-700">
                  Enregistrer
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordFields(false);
                    setPasswordForm({current_password: "", new_password: "", confirm_password: ""});
                  }}
                >
                  Annuler
                </Button>
              </div>
            </form>
          )}
          
          <div className="mt-6 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleLogoutAll}
              className="text-red-600 border-red-600 hover:bg-red-50"
            >
              <PowerOff className="h-4 w-4 mr-2" />
              Se déconnecter de tous les appareils
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin Users Management (Super Admin Only) */}
      {currentAdmin?.role === "super_admin" && (
        <>
          <Card className="border-rose-200">
            <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-rose-600" />
                    Comptes Administrateurs
                  </CardTitle>
                  <CardDescription>
                    Gérez les accès administrateurs secondaires
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowNewAdminForm(!showNewAdminForm)}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter un admin
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* New Admin Form */}
              {showNewAdminForm && (
                <form onSubmit={handleCreateAdmin} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                  <h3 className="font-semibold text-lg">Nouvel Administrateur</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom *</Label>
                      <Input
                        id="name"
                        value={newAdminForm.name}
                        onChange={(e) => setNewAdminForm({...newAdminForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newAdminForm.email}
                        onChange={(e) => setNewAdminForm({...newAdminForm, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="password">Mot de passe *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newAdminForm.password}
                        onChange={(e) => setNewAdminForm({...newAdminForm, password: e.target.value})}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Min. 8 caractères, 1 majuscule, 1 chiffre, 1 symbole
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="role">Rôle</Label>
                      <select
                        id="role"
                        value={newAdminForm.role}
                        onChange={(e) => setNewAdminForm({...newAdminForm, role: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="admin">Administrateur</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit" className="bg-rose-600 hover:bg-rose-700">
                      Créer l'administrateur
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewAdminForm(false);
                        setNewAdminForm({name: "", email: "", password: "", role: "admin"});
                      }}
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              )}

              {/* Admin Users List */}
              <div className="space-y-3">
                {adminUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`p-4 border rounded-lg ${user.active ? 'bg-white' : 'bg-gray-100'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-semibold text-lg">{user.name}</h4>
                          {user.role === "super_admin" && (
                            <span className="px-2 py-1 bg-rose-100 text-rose-700 rounded text-xs font-semibold">
                              SUPER ADMIN
                            </span>
                          )}
                          {!user.active && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded text-xs font-semibold">
                              DÉSACTIVÉ
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Dernière connexion: {user.last_login 
                            ? format(new Date(user.last_login), "dd MMM yyyy HH:mm", { locale: fr })
                            : "Jamais"}
                        </p>
                      </div>
                      
                      {user.role !== "super_admin" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleAdmin(user.id, user.active)}
                            className={user.active ? "text-orange-600" : "text-green-600"}
                          >
                            {user.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAdmin(user.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Activity Logs */}
          <Card className="border-rose-200">
            <CardHeader className="bg-gradient-to-r from-rose-50 to-pink-50">
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-rose-600" />
                Journal d'Activité
              </CardTitle>
              <CardDescription>
                Les 20 dernières actions administratives
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {adminLogs.map((log) => (
                  <div key={log.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{log.action}</p>
                        <p className="text-gray-600">{log.admin_email}</p>
                        {log.details && <p className="text-gray-500 text-xs mt-1">{log.details}</p>}
                      </div>
                      <p className="text-xs text-gray-500">
                        {format(new Date(log.timestamp), "dd MMM HH:mm", { locale: fr })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminSettingsPage;
